import React, { useState, useEffect } from "react";
import { Button, Grid, MenuItem, Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { makeStyles } from "@mui/styles";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { UpdateBeneficialOfficerDTO } from "@inosio/payments-sdk";
import { format } from "date-fns";
import CustomDropdown from "../../../components/customDropdown";
import { useStyles } from "../../../style/Pages/BasicCompanyInfo";
import { removeDashes } from "../../shared/util/helpers";
import { approvedISOStandards } from "../../../shared/util/approvedISOStandards";
import { selectOnboardingApplicationState, updateIndividualBeneficialOwner } from "../sagas/pmonboardingSlice";
import { schemaValidationBeneficialOwnerData, schemaValidationBeneficialOwnerDataType } from "../../../utils/validation";
import { InputField } from "../../../components/InputController";

interface Props {
  index: number;
  benOwner: schemaValidationBeneficialOwnerDataType;
  openAccordion?: boolean;
  setShowBenOwnerForm?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useStyle = makeStyles(() => ({
  accordionStyle: {
    borderRadius: "24px !important",
    overflow: "hidden",
  },
}));

const textStyle = {
  fontWeight: "bold",
};
export const FormBeneficialOwners = (props: Props) => {
  // benOwnwer info Saved Values
  const savedValues: schemaValidationBeneficialOwnerDataType = props.benOwner;
  savedValues.country = savedValues.country || 'US';
  savedValues.state = savedValues.state || approvedISOStandards.States[0].Code;

  const formik = useFormik<schemaValidationBeneficialOwnerDataType>({
    initialValues: savedValues,
    validationSchema: schemaValidationBeneficialOwnerData,
    onSubmit: async (values) => {
      dispatch({
        type: updateIndividualBeneficialOwner.type,
        payload: {
          index: props.index,
          data: {
            MiddleName: values.middle_name,
            Dob: format(new Date(values.dob), "yyyy-MM-dd"), //expected: MM/dd/yyyy, converted to YYYY-MM-DD for api
            PhysicalAddress: {
              City: values.city,
              State: values.state,
              Zip: values.zip,
              Line1: values.physical_address,
              Country: values.country,
            },
            FirstName: values.first_name,
            LastName: values.last_name,
            Ssn: removeDashes(values.ssn),
            Email: values.email,
            PhoneNumber: removeDashes(values.phone),
            OwnershipPercentage: values.ownership_amount,
            PassportIdNumber: values.passport_number,
          } as UpdateBeneficialOfficerDTO,
        },
      });
      props.setShowBenOwnerForm && props.setShowBenOwnerForm(false); // close form
    },
    enableReinitialize: true,
  });
  const classes = useStyle();
  const classB = useStyles();
  const dispatch = useDispatch();
  const { onboarding } = useSelector(selectOnboardingApplicationState);

  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;
  const [selectedState, setSelectedState] = useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue("state", "");
    formik.setFieldValue("state_of_incorporation", "");
    setSelectedState("");
  };

  // load selected state if a valid selection was made originally, else let plain text box render state of origin
  useEffect(() => {
    formik.values.country && ["US", "CA"].includes(formik.values.country) && approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.state) && setSelectedState(formik.values.state);
  }, [onboarding, formik]);

  const [totalOwnership, setTotalOwnership] = useState(0);
  useEffect(() => {
    const ownership = (onboarding?.PrincipalOfficer?.OwnershipPercentage || 0) + (onboarding?.BeneficialOwners?.map((benOwner) => benOwner.OwnershipPercentage || 0).reduce((prev, next) => prev + next, 0) || 0);
    setTotalOwnership(ownership - (savedValues.ownership_amount || 0));
  }, [onboarding, savedValues])

  const [ownershipAmountError, setOwnershipAmountError] = useState('');
  const validateTotalOwnership = (ownershipValue: number) => (+ownershipValue + +totalOwnership) <= +100;
  useEffect(() => {
    if (!validateTotalOwnership(formik?.values?.ownership_amount)) {
      setOwnershipAmountError("Total ownership must be less than 100%");
    } else {
      setOwnershipAmountError('');
    }
  }, [formik])

  return (
    <Box sx={{ marginTop: "3%" }}>
      <form onSubmit={formik.handleSubmit}>
        <Accordion className={classes.accordionStyle} expanded={props.openAccordion}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
            <Typography sx={textStyle}>{`Beneficial Owner ${props.index + 1} Info`}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={0.5} direction="row">
              <Grid item md={6}>
                <InputField
                  name={"first_name"}
                  label={"First name"}
                  isError={false}
                  errors={formik.errors?.first_name}
                  touched={formik.touched?.first_name}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("first_name", e.target?.value)}
                  value={formik.values.first_name || ""}
                />
              </Grid>
              <Grid item md={6}>
                <InputField
                  name={"middle_name"}
                  errors={formik.errors?.middle_name}
                  touched={formik.touched?.middle_name}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("middle_name", e.target?.value)}
                  label={"Middle name"}
                  value={formik.values.middle_name || ""}
                />
              </Grid>
              <Grid item md={6}>
                <InputField
                  name={"last_Name"}
                  errors={formik.errors?.last_name}
                  touched={formik.touched?.last_name}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("last_name", e.target?.value)}
                  label={"Last name"}
                  value={formik.values.last_name || ""}
                />
              </Grid>
              <Grid item md={6}>
                <InputField
                  name={"dob"}
                  errors={formik.errors?.dob}
                  touched={formik.touched?.dob}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("dob", e.target?.value)}
                  label={"Date of Birth"}
                  value={formik.values.dob || ""}
                  placeholder={`MM/dd/yyyy`}
                />
              </Grid>
            </Grid>
            <InputField
              name={"ssn"}
              isError={false}
              label={"SSN*"}
              errors={formik.errors?.ssn}
              touched={formik.touched?.ssn}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("ssn", e.target?.value)}
              value={formik.values.ssn || ""}
            />
            <InputField
              name={"email"}
              isError={false}
              label={"Email*"}
              errors={formik.errors?.email}
              touched={formik.touched?.email}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("email", e.target?.value)}
              value={formik.values.email || ""}
            />
            <InputField
              name={"phone"}
              isError={false}
              label={"Phone*"}
              errors={formik.errors?.phone}
              touched={formik.touched?.phone}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("phone", e.target?.value)}
              value={formik.values.phone || ""}
            />
            <InputField
              name={"physical_address"}
              isError={false}
              label={"Physical Address*"}
              errors={formik.errors?.physical_address}
              touched={formik.touched?.physical_address}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("physical_address", e.target?.value)}
              value={formik.values.physical_address || ""}
            />
            <Grid container spacing={1} direction="row">
              <Grid item md={6}>
                <InputField
                  name={"city"}
                  placeholder={"City"}
                  label={"City"}
                  isError={false}
                  errors={formik.errors?.city}
                  touched={formik.touched?.city}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("city", e.target?.value)}
                  value={formik.values.city || ""}
                />
              </Grid>
              <Grid item md={6}>
                {formik.values.country && ["US", "CA"].includes(formik.values.country) ? (
                  <>
                    <CustomDropdown
                      id={"state-dropdown"}
                      isFullWidth
                      label={"State"}
                      labelId={""}
                      value={selectedState}
                      handleChange={(e) => {
                        setSelectedState(e.target?.value);
                        formik.setFieldValue("state", e.target?.value);
                      }}
                    >
                      {formik.values.country === "US"
                        ? approvedStates.map((stateRecord) => (
                            <MenuItem style={{ height: "36px" }} key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + " - " + stateRecord.Code}
                            </MenuItem>
                          ))
                        : approvedProvinces.map((provinceRecord) => (
                            <MenuItem style={{ height: "36px" }} key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + " - " + provinceRecord.Code}
                            </MenuItem>
                          ))}{" "}
                    </CustomDropdown>
                  </>
                ) : (
                  <InputField
                    value={formik.values.state || ""}
                    name={"state"}
                    isError={false}
                    errors={formik.errors?.state}
                    touched={formik.touched?.state}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue("state", e.target?.value)}
                    placeholder={"State"}
                    label={"State"}
                  />
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1} direction="row">
              <Grid item md={6}>
                <InputField
                  name={"zip"}
                  placeholder={"Zip"}
                  label={"Zip"}
                  isError={false}
                  errors={formik.errors?.zip}
                  touched={formik.touched?.zip}
                  onBlur={formik.handleBlur}
                  onChange={(e) => formik.setFieldValue("zip", e.target?.value)}
                  value={formik.values.zip || ""}
                />
              </Grid>
              <Grid item md={6}>
                <CustomDropdown
                  label={"Country"}
                  isFullWidth
                  handleChange={(e: any) => {
                    resetStateSelection();

                    // update selected country
                    formik.setFieldValue("country", e.target?.value);
                  }}
                  value={formik.values.country || ""}
                >
                  {approvedCountries.map((countryRecord) => (
                    <MenuItem style={{ height: "36px" }} key={countryRecord.Code} value={countryRecord.Code}>
                      {countryRecord.Country + " - " + countryRecord.Code}
                    </MenuItem>
                  ))}
                </CustomDropdown>
              </Grid>
            </Grid>
            <InputField
              isError={false}
              label={"Ownership Amount"}
              name={"ownership_amount"}
              errors={formik.errors?.ownership_amount || ownershipAmountError}
              touched={formik.touched?.ownership_amount}
              onBlur={formik.handleBlur}
              onChange={(e) => formik.setFieldValue("ownership_amount", parseFloat(isNaN(parseFloat(e.target?.value)) ? "0" : e.target?.value))}
              value={formik.values.ownership_amount || 0}
            />
            <InputField
              isError={false}
              label={"ID/Passport Number"}
              name={"passport_number"}
              errors={formik.errors?.passport_number}
              touched={formik.touched?.passport_number}
              onBlur={formik.handleBlur}
              value={formik.values.passport_number || ""}
              onChange={(e) => formik.setFieldValue("passport_number", e.target?.value)}
            />
            <Box display="flex" justifyContent="end">
              <Button type="submit" disabled={Boolean(!formik.isValid || !formik.dirty || !validateTotalOwnership(formik.values?.ownership_amount))} className={classB.signUpBtn}>
                <Typography className={classB.signUpBtnText}>update</Typography>
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      </form>
    </Box>
  );
};
